import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './single-flight';
import type { QueryKey } from '@tanstack/react-query';
import { useDataSource } from './data-source';
import { trackAction, tracked } from '../analytics/mutation-events';
import type { Inbox, NotificationMute, NotificationPreferences } from './types';

export const notificationKeys = {
  inbox: (tenant: string): QueryKey => ['notifications', tenant, 'inbox'],
  preferences: (tenant: string): QueryKey => ['notifications', tenant, 'preferences'],
};

/** The inbox, refreshed every minute while the page is open: the bell's count is at most a minute old. */
export function useInbox(tenant: string) {
  const ds = useDataSource();
  return useQuery({
    queryKey: notificationKeys.inbox(tenant),
    queryFn: () => ds.notifications.inbox(tenant),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkRead(tenant: string) {
  const ds = useDataSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) => ds.notifications.markRead(tenant, ids),
    onSuccess: (inbox: Inbox) => qc.setQueryData(notificationKeys.inbox(tenant), inbox),
  });
}

export function useNotificationPreferences(tenant: string) {
  const ds = useDataSource();
  return useQuery({
    queryKey: notificationKeys.preferences(tenant),
    queryFn: () => ds.notifications.preferences(tenant),
  });
}

/** Saving the document and muting a kind for a job both answer the preferences, which go straight into the cache. */
export function usePreferenceChanges(tenant: string) {
  const ds = useDataSource();
  const qc = useQueryClient();
  const set = (p: NotificationPreferences) =>
    qc.setQueryData(notificationKeys.preferences(tenant), p);
  // A switch shows its new position at once and goes back if the change did not save; a kind turned off
  // leaves the bell, so the inbox reads again.
  const save = useMutation({
    mutationFn: (p: NotificationPreferences) => ds.notifications.setPreferences(tenant, p),
    onMutate: async (p: NotificationPreferences) => {
      await qc.cancelQueries({ queryKey: notificationKeys.preferences(tenant) });
      const before = qc.getQueryData<NotificationPreferences>(notificationKeys.preferences(tenant));
      set(p);
      return { before };
    },
    onError: (_error, _p, context) => {
      if (context?.before) set(context.before);
    },
    onSuccess: (p: NotificationPreferences, _asked, context) => {
      trackKindChanges(context.before, p);
      set(p);
      void qc.invalidateQueries({ queryKey: notificationKeys.inbox(tenant) });
    },
  });
  const mute = useMutation({
    mutationFn: (m: NotificationMute) => ds.notifications.mute(tenant, m),
    onSuccess: tracked<NotificationPreferences, NotificationMute>(
      'notifications.preference_changed',
      (_p, m) => ({ kind: m.kind, on: false }),
      set,
    ),
  });
  return { save, mute };
}

/** A kind is on when any channel carries it; one event per kind whose on/off flipped. */
function trackKindChanges(
  before: NotificationPreferences | undefined,
  after: NotificationPreferences,
): void {
  // On while it reaches the person some way: the bell, or the email where Paige mails the kind to them.
  const on = (k: NotificationPreferences['kinds'][number]) =>
    k.inApp || (k.emailed === true && k.email);
  for (const kind of after.kinds) {
    const was = before?.kinds.find((k) => k.kind === kind.kind);
    if (was && on(was) === on(kind)) continue;
    trackAction('notifications.preference_changed', { kind: kind.kind, on: on(kind) });
  }
}
